/* ============================================================================
   GYA LABS — Redesigned mobile menu controller
   Self-contained <mobile-menu> custom element: drawer open/close, scroll lock,
   multi-screen stack navigator with slide transitions, and in-drawer predictive
   search. Decoupled from theme internals (only reuses the .lock scroll class).
   ========================================================================== */
(function () {
  if (window.customElements.get('mobile-menu')) return;

  var REDUCED_MOTION =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var MobileMenu = class extends HTMLElement {
    constructor() {
      super();
      this._onKeydown = this._onKeydown.bind(this);
      this._onBodyScroll = this._onScroll.bind(this);
    }

    connectedCallback() {
      // Relocating to <body> below re-fires connectedCallback; this guard makes the
      // second pass a no-op so listeners/state bind exactly once.
      if (this._initialized) return;
      this._initialized = true;

      this.overlay = this.querySelector('[data-gm-overlay]');
      this.panel = this.querySelector('[data-gm-panel]');
      this.navbar = this.querySelector('[data-gm-navbar]');
      this.backBtn = this.querySelector('[data-gm-back]');
      this.closeBtn = this.querySelector('[data-gm-close-btn]');
      this.titleEl = this.querySelector('[data-gm-title]');
      this.brandEl = this.querySelector('[data-gm-brand]');
      this.scrollBody = this.querySelector('[data-gm-scroll]');
      this.screens = {};
      Array.prototype.forEach.call(this.querySelectorAll('.gm-screen'), function (s) {
        this.screens[s.id] = s;
      }, this);

      this.stack = ['gm-home'];
      this._lastFocus = null;

      // Open via any [aria-controls="<id>"] toggle (the header hamburger).
      this._delegatedToggle = function (event) {
        var control = event.target.closest('[aria-controls="' + this.id + '"]');
        if (!control) return;
        event.preventDefault();
        this.open ? this.hide() : this.show(control);
      }.bind(this);
      document.addEventListener('click', this._delegatedToggle);

      // In-drawer navigation (push), back, close, overlay.
      this.addEventListener('click', this._onClick.bind(this));
      if (this.backBtn) this.backBtn.addEventListener('click', this.back.bind(this));
      if (this.closeBtn) this.closeBtn.addEventListener('click', this.hide.bind(this));
      if (this.overlay) this.overlay.addEventListener('click', this.hide.bind(this));
      if (this.scrollBody) this.scrollBody.addEventListener('scroll', this._onBodyScroll, { passive: true });

      this._initSearch();
      this._render('fwd', true);

      // Theme editor: open the drawer when one of its blocks is selected so the
      // merchant can see "Shop by need" / "Shop by product" edits live.
      if (window.Shopify && window.Shopify.designMode) {
        var self = this;
        document.addEventListener('shopify:block:select', function (event) {
          if (event.target && self.contains(event.target) && !self.open) self.show();
        });
      }

      // Escape the header's stacking context (z-index:4) by relocating to <body>,
      // mirroring how the theme's own drawers append themselves to the body. Done
      // last so binding above already ran; the _initialized guard skips the re-fire.
      if (this.parentElement !== document.body) {
        var existing = document.body.querySelector('#' + this.id);
        if (existing && existing !== this) existing.remove();
        document.body.appendChild(this);
      }
    }

    get open() {
      return this.hasAttribute('open');
    }

    /* ---------------- open / close ---------------- */
    show(trigger) {
      if (this.open) return;
      this._lastFocus = trigger || document.activeElement;
      this.stack = ['gm-home'];
      this.setAttribute('open', '');
      document.documentElement.classList.add('lock');
      document.addEventListener('keydown', this._onKeydown);
      this._render('fwd', true);
      // force reflow so the transition runs from the hidden state
      void this.offsetWidth;
      this.classList.add('gm-active');
      this.controls().forEach(function (c) {
        c.setAttribute('aria-expanded', 'true');
      });
      var focusTarget = this.closeBtn || this.panel;
      var self = this;
      window.setTimeout(function () {
        if (focusTarget) focusTarget.focus();
      }, 60);
    }

    hide() {
      if (!this.open) return;
      this.classList.remove('gm-active');
      document.documentElement.classList.remove('lock');
      document.removeEventListener('keydown', this._onKeydown);
      this.controls().forEach(function (c) {
        c.setAttribute('aria-expanded', 'false');
      });
      var self = this;
      var done = function () {
        self.removeAttribute('open');
      };
      if (REDUCED_MOTION) {
        done();
      } else {
        var finished = false;
        var onEnd = function () {
          if (finished) return;
          finished = true;
          self.panel.removeEventListener('transitionend', onEnd);
          done();
        };
        this.panel.addEventListener('transitionend', onEnd);
        window.setTimeout(onEnd, 400);
      }
      if (this._lastFocus && this._lastFocus.focus) this._lastFocus.focus();
    }

    controls() {
      return Array.prototype.slice.call(
        document.querySelectorAll('[aria-controls="' + this.id + '"]')
      );
    }

    /* ---------------- navigation stack ---------------- */
    _onClick(event) {
      var goEl = event.target.closest('[data-gm-go]');
      if (goEl && this.contains(goEl)) {
        event.preventDefault();
        this.go(goEl.getAttribute('data-gm-go'));
      }
    }

    go(screenId) {
      if (!this.screens[screenId]) return;
      this.stack.push(screenId);
      this._render('fwd');
    }

    back() {
      if (this.stack.length > 1) {
        this.stack.pop();
        this._render('back');
      }
    }

    _render(dir, instant) {
      var currentId = this.stack[this.stack.length - 1];
      var self = this;
      Object.keys(this.screens).forEach(function (id) {
        var screen = self.screens[id];
        var active = id === currentId;
        screen.classList.toggle('gm-screen--active', active);
        screen.classList.remove('gm-anim-fwd', 'gm-anim-back');
        if (active && !instant && !REDUCED_MOTION) {
          // restart the animation
          void screen.offsetWidth;
          screen.classList.add(dir === 'back' ? 'gm-anim-back' : 'gm-anim-fwd');
        }
      });

      var screen = this.screens[currentId];
      var isHome = screen.hasAttribute('data-gm-home');
      var title = screen.getAttribute('data-gm-screen-title') || '';

      if (this.backBtn) this.backBtn.hidden = this.stack.length <= 1;
      if (this.brandEl) this.brandEl.hidden = !isHome;
      if (this.titleEl) {
        this.titleEl.hidden = isHome;
        this.titleEl.textContent = title;
      }

      if (this.scrollBody) this.scrollBody.scrollTop = 0;
      this._setScrolled(false);

      if (currentId === 'gm-search') {
        var input = this.querySelector('.gm-search-input');
        if (input) window.setTimeout(function () { input.focus(); }, 80);
      }
    }

    /* ---------------- navbar scroll state ---------------- */
    _onScroll() {
      this._setScrolled(this.scrollBody.scrollTop > 8);
    }

    _setScrolled(on) {
      if (this.navbar) this.navbar.classList.toggle('gm-scrolled', on);
    }

    _onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        if (this.stack.length > 1) this.back();
        else this.hide();
      }
    }

    /* ---------------- predictive search ---------------- */
    _initSearch() {
      var searchScreen = this.screens['gm-search'];
      if (!searchScreen) return;
      this.searchInput = searchScreen.querySelector('.gm-search-input');
      this.searchClear = searchScreen.querySelector('.gm-search-clear');
      this.searchDefault = searchScreen.querySelector('[data-gm-search-default]');
      this.searchResults = searchScreen.querySelector('[data-gm-search-results]');
      if (!this.searchInput) return;

      var self = this;
      this._searchTimer = null;
      this._searchAbort = null;

      this.searchInput.addEventListener('input', function () {
        var q = self.searchInput.value.trim();
        if (self.searchClear) self.searchClear.hidden = q.length === 0;
        window.clearTimeout(self._searchTimer);
        if (q.length < 1) {
          self._showSearchDefault();
          return;
        }
        self._searchTimer = window.setTimeout(function () {
          self._runSearch(q);
        }, 220);
      });

      if (this.searchClear) {
        this.searchClear.hidden = true;
        this.searchClear.addEventListener('click', function () {
          self.searchInput.value = '';
          self.searchClear.hidden = true;
          self._showSearchDefault();
          self.searchInput.focus();
        });
      }

      // popular-search chips prefill the query
      searchScreen.addEventListener('click', function (event) {
        var chip = event.target.closest('[data-gm-suggest]');
        if (!chip) return;
        event.preventDefault();
        self.searchInput.value = chip.getAttribute('data-gm-suggest');
        self.searchInput.dispatchEvent(new Event('input', { bubbles: true }));
        self.searchInput.focus();
      });
    }

    _showSearchDefault() {
      if (this.searchDefault) this.searchDefault.hidden = false;
      if (this.searchResults) {
        this.searchResults.hidden = true;
        this.searchResults.innerHTML = '';
      }
    }

    _runSearch(q) {
      var self = this;
      if (this.searchDefault) this.searchDefault.hidden = true;
      if (!this.searchResults) return;
      this.searchResults.hidden = false;
      this.searchResults.innerHTML = '<div class="gm-search-loading">Searching…</div>';

      if (this._searchAbort) this._searchAbort.abort();
      this._searchAbort =
        'AbortController' in window ? new AbortController() : null;

      var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      // Render results through a Liquid section so 'amzn'-tagged products are excluded
      // server-side (the suggest.json API exposes no tags). Mirrors the theme's search.
      var url =
        root +
        'search/suggest?q=' +
        encodeURIComponent(q) +
        '&section_id=mobile-menu-search' +
        '&resources[type]=product,collection' +
        '&resources[limit]=8&resources[limit_scope]=each';

      fetch(url, this._searchAbort ? { signal: this._searchAbort.signal } : {})
        .then(function (r) { return r.text(); })
        .then(function (html) {
          if (self.searchInput.value.trim() !== q) return; // stale
          var section = new DOMParser()
            .parseFromString(html, 'text/html')
            .querySelector('.shopify-section');
          self.searchResults.innerHTML = section ? section.innerHTML : html;
        })
        .catch(function (err) {
          if (err && err.name === 'AbortError') return;
          self.searchResults.innerHTML =
            '<div class="gm-empty">Something went wrong. Please try again.</div>';
        });
    }
  };

  window.customElements.define('mobile-menu', MobileMenu);
})();
